import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Product } from '@/data/products';
import {
  WooCommerceProducts,
  WooCommerceOrders,
  WooCommerceCoupons,
  OrderDatabase,
  convertWooProductToLocal,
  CartItem,
  ShippingAddress
} from '@/services/woocommerce';

export interface ShopCartItem extends Product {
  quantity: number;
}

/** Delivery pricing — the one place these numbers are defined. */
export const FREE_DELIVERY_THRESHOLD = 650;
export const STANDARD_SHIPPING = 100;

interface AppliedCoupon {
  code: string;
  /** 'percent' discounts must be recomputed as the cart changes. */
  type: 'percent' | 'fixed';
  value: number;
}

interface ShopContextType {
  // Cart
  cart: ShopCartItem[];
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateCartQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  cartTotal: number;
  cartCount: number;
  /** Delivery cost for the current cart (0 above the free-delivery threshold). */
  shipping: number;
  /** cartTotal - discount + shipping. What the customer actually pays. */
  orderTotal: number;
  
  // Wishlist
  wishlist: string[];
  addToWishlist: (productId: string) => void;
  removeFromWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  
  // Products
  products: Product[];
  loadingProducts: boolean;
  fetchProducts: (category?: string) => Promise<void>;
  searchProducts: (query: string) => Promise<Product[]>;
  
  // Checkout
  checkout: (orderData: {
    billing: ShippingAddress & { email: string; phone: string };
    shipping: ShippingAddress;
    payment_method: string;
    customer_note?: string;
  }) => Promise<{
    success: boolean;
    orderNumber?: string;
    total?: number;
    paymentInstructions?: { method: string; reference: string; note: string };
    error?: string;
  }>;
  
  // Coupons
  applyCoupon: (code: string) => Promise<{ valid: boolean; discount?: string; message?: string }>;
  appliedCoupon: string | null;
  discount: number;
  
  // Orders
  orderHistory: any[];
  fetchOrderHistory: (email: string) => Promise<void>;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'marple_cart_v2';
const WISHLIST_STORAGE_KEY = 'marple_wishlist';

export function ShopProvider({ children }: { children: React.ReactNode }) {
  // Cart State
  const [cart, setCart] = useState<ShopCartItem[]>([]);
  const [wishlist, setWishlist] = useState<string[]>([]);
  
  // Product State
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Checkout State
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [orderHistory, setOrderHistory] = useState<any[]>([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const loadStoredData = () => {
      try {
        // Load cart
        const savedCart = localStorage.getItem(CART_STORAGE_KEY);
        if (savedCart) {
          const parsed = JSON.parse(savedCart);
          const savedAt = localStorage.getItem(`${CART_STORAGE_KEY}_date`);
          if (savedAt) {
            const daysSince = (Date.now() - parseInt(savedAt)) / (1000 * 60 * 60 * 24);
            if (daysSince < 7) {
              setCart(parsed);
            } else {
              localStorage.removeItem(CART_STORAGE_KEY);
              localStorage.removeItem(`${CART_STORAGE_KEY}_date`);
            }
          }
        }
        
        // Load wishlist
        const savedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
        if (savedWishlist) {
          setWishlist(JSON.parse(savedWishlist));
        }
      } catch (error) {
        console.error('Failed to load stored data:', error);
      }
    };
    
    loadStoredData();
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    // Must also persist the empty cart: guarding on length meant removing the
    // last item never cleared storage, so deleted items came back on reload.
    if (cart.length > 0) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
      localStorage.setItem(`${CART_STORAGE_KEY}_date`, Date.now().toString());
    } else {
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem(`${CART_STORAGE_KEY}_date`);
    }
  }, [cart]);

  // Save wishlist to localStorage
  useEffect(() => {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist));
  }, [wishlist]);

  // Computed values (must be defined before callbacks that use them)
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Single source of truth for pricing. Previously the cart drawer, the checkout
  // modal and the submitted order each computed shipping differently, so the
  // total changed between screens and the order was billed a flat R100 even when
  // the customer had qualified for free delivery.
  const shipping = cart.length === 0 || cartTotal >= FREE_DELIVERY_THRESHOLD ? 0 : STANDARD_SHIPPING;

  // Derived, not stored: a stored discount went stale when the cart changed
  // afterwards, which could render a negative total.
  const discount = appliedCoupon
    ? appliedCoupon.type === 'percent'
      ? (cartTotal * appliedCoupon.value) / 100
      : Math.min(appliedCoupon.value, cartTotal)
    : 0;

  const orderTotal = Math.max(0, cartTotal - discount) + shipping;
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Cart Actions
  const addToCart = useCallback((product: Product, quantity: number = 1) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  }, []);

  const updateCartQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(prev => prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      ));
    }
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart([]);
    setAppliedCoupon(null);
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(`${CART_STORAGE_KEY}_date`);
  }, []);

  // Wishlist Actions
  const addToWishlist = useCallback((productId: string) => {
    setWishlist(prev => {
      if (prev.includes(productId)) return prev;
      return [...prev, productId];
    });
  }, []);

  const removeFromWishlist = useCallback((productId: string) => {
    setWishlist(prev => prev.filter(id => id !== productId));
  }, []);

  const isInWishlist = useCallback((productId: string) => {
    return wishlist.includes(productId);
  }, [wishlist]);

  // Product Actions
  const fetchProducts = useCallback(async (category?: string) => {
    setLoadingProducts(true);
    try {
      const wooProducts = await WooCommerceProducts.getAll({
        per_page: 50,
        category
      });
      const localProducts = wooProducts.map(convertWooProductToLocal);
      setProducts(localProducts);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      // Fallback to local data if WooCommerce is unavailable
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const searchProducts = useCallback(async (query: string): Promise<Product[]> => {
    try {
      const wooProducts = await WooCommerceProducts.getAll({
        search: query,
        per_page: 20
      });
      return wooProducts.map(convertWooProductToLocal);
    } catch (error) {
      console.error('Failed to search products:', error);
      return [];
    }
  }, []);

  // Coupon Actions
  const applyCoupon = useCallback(async (code: string) => {
    try {
      const result = await WooCommerceCoupons.validate(code);
      if (result.valid) {
        // The API returns the type in `discount_type` ('percent' | 'fixed_cart' |
        // 'fixed_product'); `discount` is a bare amount with no '%' in it, so the
        // old `discount.includes('%')` check never matched and percentage coupons
        // were applied as a flat rand amount.
        const isPercent = result.discount_type === 'percent'
          || (result.discount ?? '').includes('%');
        const value = parseFloat((result.discount ?? '0').replace(/[^0-9.]/g, '')) || 0;
        setAppliedCoupon({ code, type: isPercent ? 'percent' : 'fixed', value });
      }
      return result;
    } catch (error) {
      return { valid: false, message: 'Error validating coupon' };
    }
  }, []);

  // Checkout Action
  const checkout = useCallback(async (orderData: {
    billing: ShippingAddress & { email: string; phone: string };
    shipping: ShippingAddress;
    payment_method: string;
    customer_note?: string;
  }) => {
    try {
      if (cart.length === 0) {
        return { success: false, error: 'Your cart is empty.' };
      }

      // Post to our own checkout endpoint. We deliberately send only the
      // product identity and quantity — the server re-prices every line from
      // the products table, so nothing here can influence what is charged.
      const API_BASE = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
      const response = await fetch(`${API_BASE}/checkout/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.map(item => ({ slug: item.id, quantity: item.quantity })),
          email: orderData.billing.email,
          phone: orderData.billing.phone,
          shipping: {
            first_name: orderData.shipping.first_name,
            last_name: orderData.shipping.last_name,
            company: orderData.shipping.company || undefined,
            address_1: orderData.shipping.address_1,
            address_2: orderData.shipping.address_2 || undefined,
            city: orderData.shipping.city,
            state: orderData.shipping.state,
            postcode: orderData.shipping.postcode,
            country: orderData.shipping.country || 'ZA',
          },
          payment_method: orderData.payment_method === 'bacs' ? 'eft' : orderData.payment_method,
          discount_code: appliedCoupon?.code,
          customer_note: orderData.customer_note,
        }),
      });

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        return {
          success: false,
          error: result?.detail || 'We could not place your order. Please try again.',
        };
      }

      // Only clear once the order is confirmed. Clearing earlier left a failed
      // checkout with an empty cart and no way back to it.
      clearCart();

      return {
        success: true,
        orderNumber: result.order_number,
        total: result.total_amount,
        paymentInstructions: result.payment_instructions,
      };
    } catch (error) {
      console.error('Checkout failed:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Checkout failed' 
      };
    }
  }, [cart, appliedCoupon, clearCart]);

  // Order History
  const fetchOrderHistory = useCallback(async (email: string) => {
    try {
      const orders = await OrderDatabase.getCustomerOrders(email);
      setOrderHistory(orders);
    } catch (error) {
      console.error('Failed to fetch order history:', error);
    }
  }, []);

  const value: ShopContextType = {
    cart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    cartTotal,
    cartCount,
    shipping,
    orderTotal,
    wishlist,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    products,
    loadingProducts,
    fetchProducts,
    searchProducts,
    checkout,
    applyCoupon,
    appliedCoupon: appliedCoupon?.code ?? null,
    discount,
    orderHistory,
    fetchOrderHistory
  };

  return (
    <ShopContext.Provider value={value}>
      {children}
    </ShopContext.Provider>
  );
}

export function useShop() {
  const context = useContext(ShopContext);
  if (context === undefined) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
}

export default ShopContext;
